import { TextContentElement } from '@deltek/specification-client/core/Models/textContentElement'
import {
    findNodeById,
    findParentNode,
} from 'components/WorkspaceEditor/features/promoteDemote/utils/nodeHelpers'
import { useContentItemViewNode } from 'components/WorkspaceEditor/hooks'
import { useNotification } from 'components/WorkspaceEditor/services'
import { NotificationContextProps } from 'components/WorkspaceEditor/services/notificationService'
import { useCallback } from 'react'
import type {
    EditorRef,
    FocusManager,
    NotificationHandler,
    UseContentValidationReturn,
    ValidationContext,
    ValidationStrategy,
} from '../types'
import {
    articleDuplicateValidator,
    paragraphKeyDuplicateValidator,
} from '../utils/articleDuplicateValidator'

const ERROR_NOTIFICATION_DURATION_MS = 5000
const FOCUS_DELAY_MS = 100

/**
 * Creates notification handler for error messages
 */
const notificationHandler = (
    notification: NotificationContextProps
): NotificationHandler => ({
    showError: (message: string, duration = ERROR_NOTIFICATION_DURATION_MS) => {
        notification.error({ message, duration })
    },
})

/**
 * Creates editor focus manager with delay
 */
const focusManager = (): FocusManager => ({
    focusEditor: (editorRef?: EditorRef) => {
        setTimeout(() => {
            if (editorRef?.current?.focus) {
                editorRef.current.focus()
            }
        }, FOCUS_DELAY_MS)
    },
})

/** Validators run in order; first failure stops the pipeline */
const validators: ValidationStrategy[] = [
    articleDuplicateValidator,
    paragraphKeyDuplicateValidator,
]

/**
 * Hook for validating duplicate content (article names + paragraph keys).
 * Article validation runs for all workspaces; paragraph key validation is OM-only.
 * Validators are self-gating — each decides whether it applies based on the context.
 */
export const useDuplicateArticleValidation = (): UseContentValidationReturn => {
    const notification = useNotification()
    const { nodeToAdd, contentItemViewNodes } = useContentItemViewNode()

    /**
     * Builds a full validation context and runs all validators in sequence.
     * First validation failure stops the pipeline, shows an error, and refocuses the editor.
     */
    const validateAndHandle = useCallback(
        async (context: ValidationContext): Promise<boolean> => {
            if (!contentItemViewNodes?.length) return true

            const { textElement, isAddOperation, editorRef } = context

            // Resolve current node and parent once for all validators
            const currentNode = findNodeById(
                contentItemViewNodes,
                textElement.id
            )
            const parentNode = currentNode
                ? findParentNode(contentItemViewNodes, currentNode)
                : undefined

            const nodeType = isAddOperation
                ? nodeToAdd?.type || ''
                : currentNode?.textType || ''

            // Build enriched context shared by all validators
            const ctx: ValidationContext = {
                ...context,
                contentNodes: contentItemViewNodes,
                nodeType,
                parentNode,
            }

            const notify = notificationHandler(notification)
            const focus = focusManager()

            for (const validator of validators) {
                try {
                    const result = validator.validate(ctx)
                    if (!result.isValid && result.errorMessage) {
                        notify.showError(result.errorMessage)
                        focus.focusEditor(editorRef)
                        return false
                    }
                } catch (error) {
                    console.warn('Content validation error:', error)
                }
            }

            return true
        },
        [contentItemViewNodes, nodeToAdd, notification]
    )

    const validateAdd = useCallback(
        (
            text: string,
            textElement: TextContentElement,
            editorRef?: EditorRef
        ) =>
            validateAndHandle({
                text,
                textElement,
                isAddOperation: true,
                editorRef,
            }),
        [validateAndHandle]
    )

    const validateUpdate = useCallback(
        (
            text: string,
            textElement: TextContentElement,
            editorRef?: EditorRef
        ) =>
            validateAndHandle({
                text,
                textElement,
                isAddOperation: false,
                editorRef,
            }),
        [validateAndHandle]
    )

    return {
        validateAdd,
        validateUpdate,
    }
}
